/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import i18n from "../../i18n.js";
/**
 * @fileoverview Proposal logic module.
 * Handles proposal management operations including CRUD operations,
 * filtering, approval/rejection workflows, and file handling.
 * @module logic/proposal
 */

import { getAssetFileName, refreshToken, signOut } from "../../utils/common.js";
import {
  apiMessageOrGeneric,
  getGenericErrorMessage,
} from "../../utils/genericErrorMessages.js";

const LOG_NEW_TOKENS = "newTokens: ";
const ERROR_REJECTING_PROPOSAL = "Error rejecting proposal:";
const FILE_SIZE_BASE = 1024;
const FILE_SIZE_DECIMALS = 2;
const UPLOAD_SIMULATED_DELAY_MS = 1500;
const REJECT_SUCCESS_VISIBLE_MS = 4700;
const REJECT_SUCCESS_SLIDE_MS = 300;

/**
 * Fetches all proposals from the API.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of proposal objects
 */
export const getProposals = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}proposal`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      logger.info(LOG_NEW_TOKENS, { data: newTokens });
      if (!newTokens) {
        await signOut();
        navigate("/");
        return undefined;
      }
      return undefined;
    }
    return data;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Shared PUT for proposal approval, highlight, and rejection.
 * A failed token refresh still returns undefined. A later failure is logged and rethrown.
 */
const putProposalJson = async ({ url, body, navigate, retry, errorMessage }) => {
  const options = {
    method: "PUT",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(body),
  };

  try {
    const response = await fetch(url, options);
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return undefined;
      }
      return await retry();
    }

    if (!response.ok) {
      throw new Error(apiMessageOrGeneric(data?.message));
    }

    return data;
  } catch (error) {
    logger.error(errorMessage, { err: sanitizeError(error) });
    throw error;
  }
};

/**
 * Updates a proposal's approval status.
 * @async
 * @param {string} proposalId - Proposal identifier
 * @param {boolean} aprobado - Approval status
 * @param {Function} navigate - React Router navigation function
 * @param {string|null} rejectionReason - Reason for rejection (if rejected)
 * @returns {Promise<Object>} Updated proposal data
 * @throws {Error} If update fails
 */
export const updateProposal = async (
  proposalId,
  aprobado,
  navigate,
  rejectionReason,
) =>
  putProposalJson({
    url: `${process.env.REACT_APP_ROOT_API}proposal/${proposalId}`,
    body: {
      aprobado,
      ...(rejectionReason && { razonDeRechazo: rejectionReason }),
    },
    navigate,
    retry: () =>
      updateProposal(proposalId, aprobado, navigate, rejectionReason),
    errorMessage: "Error updating proposal:",
  });

/**
 * Toggles a proposal's highlighted/starred status.
 * @async
 * @param {string} proposalId - Proposal identifier
 * @param {Function} navigate - React Router navigation function
 * @param {boolean} isStarred - Whether to highlight the proposal
 * @returns {Promise<Object>} Updated proposal data
 * @throws {Error} If update fails
 */
export const handleProposalHighlight = async (
  proposalId,
  navigate,
  isStarred,
) =>
  putProposalJson({
    url: `${process.env.REACT_APP_ROOT_API}proposal/${proposalId}/highlight`,
    body: { esDestacado: isStarred },
    navigate,
    retry: () => handleProposalHighlight(proposalId, navigate, isStarred),
    errorMessage: "Error updating proposal:",
  });

/**
 * Sorts proposals with highlighted ones first.
 * @param {Array} proposals - Array of proposal objects
 * @returns {Array} Sorted array with highlighted proposals first
 */
export const sortProposalsByDestacado = (proposals) => {
  return [...proposals].sort((a, b) => {
    if (a.esDestacado === b.esDestacado) {
      return 0;
    }
    return a.esDestacado ? -1 : 1;
  });
};

/**
 * Filter functions for proposals by status.
 * @property {Function} pending - Filters pending proposals
 * @property {Function} accepted - Filters accepted proposals
 * @property {Function} rejected - Filters rejected proposals
 */
export const filterProposals = {
  pending: (proposals) =>
    proposals.filter((proposal) => proposal.aprobacionPendiente === true),
  accepted: (proposals) =>
    proposals.filter(
      (proposal) =>
        proposal.aprobado === true && proposal.aprobacionPendiente === false,
    ),
  rejected: (proposals) =>
    proposals.filter(
      (proposal) =>
        proposal.aprobado === false && proposal.aprobacionPendiente === false,
    ),
};

/**
 * Fetches active categories from the API.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of category objects
 */
export const getCategories = async (navigate) => {
  const token = localStorage.getItem("token");

  if (!token) {
    return [];
  }

  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}category/active`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getCategories(navigate);
    }
    return data;
  } catch (error) {
    logger.error("Error fetching categories:", { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Fetches a sector by its ID.
 * @async
 * @param {string} sectorId - Sector identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|string>} Sector data or empty string on error
 */
export const getSectorById = async (sectorId, navigate) => {
  const token = localStorage.getItem("token");

  if (!token) {
    return "";
  }

  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}sector/${sectorId}`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return "";
      }
      return await getSectorById(navigate, sectorId);
    }
    return data;
  } catch (error) {
    logger.error("Error fetching sector:", { err: sanitizeError(error) });
    return "";
  }
};

/**
 * Handles various proposal actions (approve, reject, star, unstar).
 * @async
 * @param {string} proposalId - Proposal identifier
 * @param {string} action - Action type ('approve', 'reject', 'star', 'unstar')
 * @param {Array} proposals - Array of proposals for finding selected
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setSelectedProposal - State setter for selected proposal
 * @param {Function} setShowRejectModal - State setter for reject modal visibility
 * @param {Function} fetchData - Function to refresh data after action
 * @returns {Promise<void>}
 */
export const handleProposalAction = async (
  proposalId,
  action,
  proposals,
  navigate,
  setSelectedProposal,
  setShowRejectModal,
  fetchData,
) => {
  if (action === "reject") {
    const proposal = proposals.find((p) => p._id === proposalId);
    setSelectedProposal(proposal);
    setShowRejectModal(true);
  } else if (action === "approve") {
    try {
      await updateProposal(proposalId, true, navigate, null);
      await fetchData();
    } catch (error) {
      logger.error("Error approving proposal:", { err: sanitizeError(error) });
    }
  } else if (action === "star") {
    try {
      await handleProposalHighlight(proposalId, navigate, true);
      await fetchData();
    } catch (error) {
      logger.error("Error starring proposal:", { err: sanitizeError(error) });
    }
  } else if (action === "unstar") {
    try {
      await handleProposalHighlight(proposalId, navigate, false);
      await fetchData();
    } catch (error) {
      logger.error("Error unstarring proposal:", { err: sanitizeError(error) });
    }
  } else {
    // Unsupported proposal action. No action required.
  }
};

/**
 * Handles post-rejection submission logic with success message.
 * @async
 * @param {Function} fetchData - Function to refresh data
 * @param {Function} setShowSuccessMessage - State setter for success message
 * @param {Function} setIsSlidingUp - State setter for slide animation
 * @returns {Promise<void>}
 */
export const handleRejectSubmit = async (
  fetchData,
  setShowSuccessMessage,
  setIsSlidingUp,
) => {
  try {
    await fetchData();
    setShowSuccessMessage(true);
    setIsSlidingUp(false);

    setTimeout(() => {
      setIsSlidingUp(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, REJECT_SUCCESS_SLIDE_MS);
    }, REJECT_SUCCESS_VISIBLE_MS);
  } catch (error) {
    logger.error(ERROR_REJECTING_PROPOSAL, { err: sanitizeError(error) });
  }
};

/**
 * Toggles a requirement's active status.
 * @async
 * @param {string} requirementId - Requirement identifier
 * @param {boolean} isActive - Current active status
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<boolean>} New active status
 */
export const handleToggleActive = async (
  _requirementId,
  isActive,
  _navigate,
) => {
  // TODO(SCONN-76): Add API call to update requirement status
  return !isActive;
};

/**
 * Capitalizes the first letter of a string.
 * @param {string} string - Input string
 * @returns {string} String with first letter capitalized
 */
export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * Gets a category's title by its ID.
 * @param {Array} categories - Array of category objects
 * @param {string} categoryId - Category identifier
 * @returns {string} Category name or default message if not found
 */
export const getCategoryTitle = (categories, categoryId) => {
  const category = categories.find((cat) => cat._id === categoryId);
  return category?.nombre || i18n.t("common.categoryNotFound");
};

/**
 * Sends a proposal rejection with reason and closes modal.
 * @async
 * @param {string} proposalId - Proposal identifier
 * @param {string} rejectionReason - Reason for rejection
 * @param {Function} navigate - React Router navigation function
 * @param {Function} onClose - Callback to close the modal
 * @returns {Promise<void>}
 */
export const handleRejectSend = async (
  proposalId,
  rejectionReason,
  navigate,
  onClose,
) => {
  try {
    await updateProposal(proposalId, false, navigate, rejectionReason);
    onClose();
  } catch (error) {
    logger.error(ERROR_REJECTING_PROPOSAL, { err: sanitizeError(error) });
  }
};

/**
 * Extracts the file name from a URL.
 * @param {string} url - File URL
 * @returns {string} Extracted file name or default message
 */
export const getFileNameFromUrl = (url) => {
  return getAssetFileName(url) || "Sin nombre";
};

/**
 * Rejects a proposal with a reason via API call.
 * @async
 * @param {string} proposalId - Proposal identifier
 * @param {string} rejectionReason - Reason for rejection
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object>} Updated proposal data
 * @throws {Error} If rejection fails
 */
export const handleRejectProposal = async (
  proposalId,
  rejectionReason,
  navigate,
) =>
  putProposalJson({
    url: `${process.env.REACT_APP_ROOT_API}proposal/${proposalId}`,
    body: {
      aprobado: false,
      razonDeRechazo: rejectionReason,
    },
    navigate,
    retry: () => handleRejectProposal(proposalId, rejectionReason, navigate),
    errorMessage: ERROR_REJECTING_PROPOSAL,
  });

/**
 * Formats file size in bytes to human-readable format.
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) {
    return "0 Bytes";
  }
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(FILE_SIZE_BASE));
  const sized = parseFloat(
    (bytes / Math.pow(FILE_SIZE_BASE, i)).toFixed(FILE_SIZE_DECIMALS),
  );
  return `${sized} ${sizes[i]}`;
};

/**
 * Checks if a company has already submitted a proposal for a requirement.
 * @param {Array} proposals - Array of proposal objects
 * @param {string} requirementId - Requirement identifier
 * @param {string} userRFC - Company RFC to check
 * @returns {boolean} True if company has submitted a proposal
 */
export const hasCompanySubmittedProposal = (
  proposals,
  requirementId,
  userRFC,
) => {
  if (!proposals || !requirementId || !userRFC) {
    return false;
  }

  return proposals.some(
    (proposal) =>
      proposal.idRequerimiento === requirementId &&
      proposal.RFCEmpresa === userRFC,
  );
};

/**
 * Handles file upload for proposal quotation.
 * @async
 * @param {File} file - File to upload
 * @param {Function} setFormData - State setter for form data
 * @param {Function} setError - State setter for error messages
 * @param {Function} setIsUploading - State setter for upload indicator
 * @returns {Promise<void>}
 */
export const handleFileUpload = async (
  file,
  setFormData,
  setError,
  setIsUploading,
) => {
  try {
    setIsUploading(true);
    setError("");
    // Simulate upload delay
    await new Promise((resolve) => setTimeout(resolve, UPLOAD_SIMULATED_DELAY_MS));
    setFormData((prev) => ({
      ...prev,
      cotizacion: file,
    }));
  } catch (error) {
    setError(i18n.t("errors.loading.proposalUploadFailed"));
  } finally {
    setIsUploading(false);
  }
};

/**
 * Creates a new proposal with optional file attachment.
 * @async
 * @param {Object} formData - Form data including descripcion, tiempoDeEntrega, costo, cotizacion
 * @param {Object} requirement - Requirement object with _id
 * @param {Function} onClose - Callback to close the modal on success
 * @param {Function} setError - State setter for error messages
 * @param {Function} setIsSubmitting - State setter for submit indicator
 * @param {string} companyRFC - Company RFC submitting the proposal
 * @returns {Promise<boolean>} True if creation successful
 */
export const createProposal = async (
  formData,
  requirement,
  onClose,
  setError,
  setIsSubmitting,
  companyRFC,
  perfilSeleccionado = 0,
) => {
  try {
    setIsSubmitting(true);
    let base64Quotation = null;
    let fileName = null;
    if (formData.cotizacion) {
      const reader = new FileReader();
      base64Quotation = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(formData.cotizacion);
      });
      fileName = formData.cotizacion.name;
    }

    const proposalData = {
      userId: localStorage.getItem("userId"),
      requirementId: requirement?._id,
      description: formData.descripcion,
      deliveryTime: formData.tiempoDeEntrega,
      cost: formData.costo,
      quotation: base64Quotation,
      fileName: fileName,
      createdAt: new Date().toISOString(),
      companyRFC,
      perfilSeleccionado,
    };

    const response = await fetch(`${process.env.REACT_APP_ROOT_API}proposal`, {
      method: "POST",
      headers: {
        "Content-Type": CONTENT_TYPE_JSON,
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(proposalData),
    });

    if (!response.ok) {
      throw new Error(getGenericErrorMessage("FAILED_TO_CREATE_PROPOSAL"));
    }

    onClose();
    return true;
  } catch (error) {
    logger.error("Error submitting proposal:", { err: sanitizeError(error) });
    setError(i18n.t("errors.loading.proposalSubmitFailed"));
    return false;
  } finally {
    setIsSubmitting(false);
  }
};
